import { AloftPrice, applyRoundingAndThresholds } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectAdjustmentSettings, selectCompSequence } from '../orderSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCarportAdj, selectCarportAdjs } from './selectCarportAdj.ts';
import { selectDetachedGarageAdj, selectDetachedGarageAdjs } from './selectDetachedGarageAdj.ts';
import { selectGarageAdj, selectGarageAdjs } from './selectGarageAdj.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateGarageCarportAdj = (
  carportAdj: AloftPrice = AloftPrice.zero,
  detachedGarageAdj: AloftPrice = AloftPrice.zero,
  garageAdj: AloftPrice = AloftPrice.zero,
) =>
  [carportAdj, detachedGarageAdj, garageAdj].reduce(
    (acc: AloftPrice, curr) => acc.add(curr),
    AloftPrice.zero,
  );

const selectGarageCarportAdj = createPriceSelector(
  [selectCarportAdj, selectDetachedGarageAdj, selectGarageAdj],
  calculateGarageCarportAdj,
);

const selectGarageCarportAdjs = createSelector(
  [selectCarportAdjs, selectDetachedGarageAdjs, selectGarageAdjs, selectCompSequence],
  (carportAdjs, detachedGarageAdjs, garageAdjs, compSequence) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateGarageCarportAdj(
        carportAdjs[propID],
        detachedGarageAdjs[propID],
        garageAdjs[propID],
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedGarageCarportAdj = createPriceSelector(
  [selectGarageCarportAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'garageCarport', adjSettings),
);

export const selectAppliedGarageCarportAdjDisplay = createSelector(
  [selectAppliedGarageCarportAdj],
  (adj) => {
    if (!adj) return undefined;

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);

export const selectAppliedGarageCarportAdjsDisplay = createSelector(
  [selectGarageCarportAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (garageCarportAdjs, adjSettings, manualAdjustmentEntities) =>
    Object.keys(garageCarportAdjs).reduce(
      (acc: Record<string, { value: string; color: string }>, propID) => {
        const adj = garageCarportAdjs[propID];
        const manualAdjustment = manualAdjustmentEntities[propID]?.garageCarport;

        if (manualAdjustment) {
          const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
          acc[propID] = {
            value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
            color: getColorFromAdjustment(manualAdjustmentPrice),
          };
        } else if (adj) {
          acc[propID] = {
            value: getStringValueFromAdjustment(
              applyRoundingAndThresholds(garageCarportAdjs[propID], 'garageCarport', adjSettings),
            ),
            color: getColorFromAdjustment(garageCarportAdjs[propID]),
          };
        }

        return acc;
      },
      {},
    ),
);
